import { GOOGLE_MAP_API_URLS } from '@/definitions';
import { logWebHrError } from "@/core/logger/log-wrapper";

let { jsonp } = require('@/core/request');

let mapInstance;
const getMapInstance = async () => {
  if (mapInstance) {
    return mapInstance;
  }

  try {
    if ((window && !window.google) || !window.google.maps) {
      await jsonp(GOOGLE_MAP_API_URLS.UK);
    }
  } catch (err) {
    logWebHrError({
      message: 'Google Maps API Error',
      source: 'address_uk',
      error: err,
    });
    throw new Error('maps library not found');
  }

  mapInstance = new window.google.maps.places.AutocompleteService();
  mapInstance._map = new window.google.maps.Map(document.createElement('div'));
  mapInstance._placeService = new window.google.maps.places.PlacesService(
    mapInstance._map
  );
  mapInstance.get = (placeId) => {
    return new Promise((resolve) => {
      mapInstance._placeService.getDetails({ placeId }, (result, status) => {
        if (result && result.address_components && status === 'OK') {
          return resolve(normalizeAddressComponents(result.address_components));
        }

        return resolve(null);
      });
    });
  };

  mapInstance.search = function mapSearch(query) {
    return new Promise((resolve, reject) => {
      mapInstance.getQueryPredictions({ input: query }, (result, status) => {
        if (status !== 'OK' && status !== 'ZERO_RESULTS') {
          return reject(status);
        }

        if (status === 'ZERO_RESULTS') {
          return resolve([]);
        }

        resolve(
          result.map(({ place_id, description }) => ({
            // eslint-disable-line camelcase
            id: place_id,
            description,
          }))
        );
      });
    });
  };

  return mapInstance;
};

getMapInstance()
  .then((mapService) => (window.mapService = mapService))
  .catch((e) => {
    logWebHrError({
      message: 'Google Maps getMapInstance Error',
      source: 'address_uk',
      error: e,
    });
  });

const normalizeAddressComponents = (components) => {
  const normalized = { addressLines: [''] };
  let subpremise = '';
  let premise = '';
  components.forEach((component) => {
    component.long_name = component.long_name.trim();
    component.short_name = component.short_name.trim();

    if (component.types.includes('subpremise')) {
      subpremise = component.long_name;
    }
    if (component.types.includes('premise')) {
      premise = component.long_name;
    }
    if (component.types.includes('street_number')) {
      normalized.addressLines[0] = normalized.addressLines[0].concat(
        component.long_name
      );
    } else if (component.types.includes('route')) {
      if (normalized.addressLines[0].length === 0) {
        normalized.addressLines[0] = component.long_name;
      } else {
        normalized.addressLines[0] = normalized.addressLines[0].concat(
          ' ',
          component.long_name
        );
      }
    } else if (component.types.includes('locality')) {
      normalized.locality = component.short_name;
    } else if (component.types.includes('administrative_area_level_1')) {
      normalized.administrativeArea = component.short_name;
    } else if (component.types.includes('administrative_area_level_2')) {
      normalized.sublocality = component.long_name;
    } else if (component.types.includes('country')) {
      normalized.regionCode = component.short_name;
    } else if (component.types.includes('postal_code')) {
      normalized.postalCode = component.short_name;
    }
  });

  if (subpremise) {
    normalized.addressLines[0] = `${subpremise}/${normalized.addressLines[0]}`;
  }

  if (premise) {
    normalized.addressLines[0] =
      `${premise} ${normalized.addressLines[0]}`.trim();
  }

  normalized.languageCode = 'EN_GB';
  return normalized;
};
/* eslint-enable */

// import debounce from '@/core/debounce';
// TODO: debounce not working :/ (added a "fake" debounce in place)
// debounce(fn, 800);
let timer;
let promise;
let finalQuery;
const external = (query) => {
  finalQuery = query;

  if (timer) {
    return promise;
  }

  return (promise = new Promise((resolve) => {
    timer = setTimeout(() => {
      clearTimeout(timer);
      timer = null;
      resolve(getMapInstance().then((maps) => maps.search(finalQuery)));
      // resolve(maps.search(finalQuery));
    }, 800);
  }));
};

const beforeInput = ({ id }) => getMapInstance().then((maps) => maps.get(id));
const beforeCreate = (data) => {
  let { addressValue } = data;

  if (!addressValue) {
    return {
      value: null,
      valueObj: null,
    };
  }

  if (typeof addressValue === 'string') {
    return { value: addressValue };
  }

  let { administrativeArea, postalCode, regionCode, addressLines, locality } =
    addressValue;

  addressValue.addressLines = addressLines || [];
  let _value = `${addressValue.addressLines[0]}, ${locality} ${administrativeArea} ${postalCode}, ${regionCode}`;  // eslint-disable-line

  if (!locality || !administrativeArea || !postalCode || !regionCode) {  // eslint-disable-line
    _value = addressValue.addressLines.join(' ');
  }

  return {
    value: _value,
    valueObj: addressValue,
  };
};

const beforeSave = (a) => {
  const { valueObj } = a;
  return { addressValue: valueObj };
};

const toPrint = (data) => {
  let { valueObj, value, addressValue } = data;

  if (value && typeof value === 'string') {
    return value;
  }

  valueObj = valueObj || addressValue;

  if (!valueObj) {
    return null;
  }

  if (typeof valueObj === 'string') {
    return valueObj;
  }

  const { locality, administrativeArea, postalCode, regionCode, addressLines} = valueObj; // eslint-disable-line
  let _value = `${addressLines[0]}${locality ? `, ${locality}` : ''}${administrativeArea ? ` ${administrativeArea}` : ''}${postalCode ? ` ${postalCode}` : ''}${regionCode ? `, ${regionCode}` : ''}`;  // eslint-disable-line

  return _value;
};

export {
  normalizeAddressComponents,
  beforeInput,
  external,
  beforeCreate,
  beforeSave,
  toPrint,
};
